import { EMAIL_MARKETING_CAMPAIGN_PATH } from "../../../utils/constants/routes";

// Campaigns Tour (key: "campaigns")
//
// 10 steps walking users through the Campaigns module:
//   1. Open Marketing submenu → spotlight Campaigns sub-tab
//   2. [center] Overview of the Campaigns page (/user/campaigns)
//   3. Spotlight the Create Campaign button
//   4. Open modal → spotlight Step 1 content (name field), name pre-filled as "Test Campaign"
//   5. Spotlight the "Send Via" section in Step 1
//   6. Advance modal to Step 2 → spotlight full Sequence & List content
//   7. Spotlight the Contact List dropdown within Step 2
//   8. Spotlight the ICP Persona section within Step 2
//   9. Advance modal to Step 3 (Skipped Contacts) → spotlight right panel
//  10. [center] Close modal → wrap-up on /user/campaigns
//
// Resume is fully supported — each step's before() independently restores all
// required state (page, modal open/closed, modal step) before the spotlight fires.

export function getCampaignsTourSteps(navigate) {
  // ── Shared helpers ──────────────────────────────────────────────────────────

  const waitForSelector = (selector, timeout = 5000) =>
    new Promise((resolve) => {
      const deadline = Date.now() + timeout;
      const check = () => {
        const el = document.querySelector(selector);
        if (el) { resolve(el); return; }
        if (Date.now() >= deadline) { resolve(null); return; }
        setTimeout(check, 100);
      };
      check();
    });

  const closeOpenMenus = async () => {
    document.querySelectorAll(".MuiBackdrop-root").forEach((el) => el.click());
    await new Promise((r) => setTimeout(r, 250));
  };

  const ensureOnCampaignsPage = async () => {
    if (window.location.pathname !== EMAIL_MARKETING_CAMPAIGN_PATH) {
      navigate(EMAIL_MARKETING_CAMPAIGN_PATH);
      await new Promise((r) => setTimeout(r, 900));
    }
  };

  // DOM sentinel per modal step — present only when that step's content is mounted.
  // step 2 has no unique sentinel (SkippedContacts has no data-tour on its root),
  // so it always dispatches; steps 0 and 1 skip the dispatch when already there.
  const MODAL_STEP_SENTINEL = {
    0: '[data-tour="campaign-basic-details-section"]',
    1: '[data-tour="campaign-sequence-list-section"]',
  };

  // Dispatches the step-jump event only when the modal isn't already on the
  // requested step. This makes 6→7, 7→8, and any same-step revisit instant.
  const setModalStep = async (step) => {
    const sentinel = MODAL_STEP_SENTINEL[step];
    if (sentinel && document.querySelector(sentinel)) return; // already there
    window.dispatchEvent(new CustomEvent("__tour:campaignSetStep", { detail: { step } }));
    await new Promise((r) => setTimeout(r, 80));
  };

  // campaign-modal-right-panel is the shared step-content wrapper — it is
  // always in the DOM while the modal is open, regardless of which modal step
  // is active. Use it as the single "is modal open" sentinel so that all steps
  // (not just step-0) get the instant fast path.
  const MODAL_OPEN_SENTINEL = '[data-tour="campaign-modal-right-panel"]';

  // Opens the Create Campaign modal and waits for it to fully animate in.
  const ensureModalOpen = async () => {
    if (document.querySelector(MODAL_OPEN_SENTINEL)) return; // already open

    // The data-tour attr is on a wrapper Box (div); the React onClick lives on
    // the inner <button>. Clicking the div does NOT propagate down to children,
    // so we must target the button element itself.
    const wrapper = document.querySelector('[data-tour="create-campaign-btn"]');
    const btn = wrapper ? (wrapper.querySelector("button") || wrapper) : null;
    if (!btn) return;
    btn.click();

    // Wait for the modal to mount, then wait for it to fully settle (position +
    // height stable) so the spotlight never fires during the entrance animation.
    await waitForSelector(MODAL_OPEN_SENTINEL, 4000);
    await waitForStable(MODAL_OPEN_SENTINEL, 1500);
  };

  // Polls the element's size AND position until both have been stable for two
  // consecutive 100ms ticks (or maxWait elapses). Checking top prevents
  // spotlighting mid-animation when a modal is still sliding in — the bounding
  // rect's top changes during the entrance transition even if the height is fixed.
  const waitForStable = async (selector, maxWait = 2500) => {
    const deadline = Date.now() + maxWait;
    let prevH = -1, prevTop = -1;
    while (Date.now() < deadline) {
      const el = document.querySelector(selector);
      const rect = el ? el.getBoundingClientRect() : null;
      const h = rect ? Math.round(rect.height) : 0;
      const top = rect ? Math.round(rect.top) : -99;
      if (h > 0 && h === prevH && top === prevTop) return;
      prevH = h;
      prevTop = top;
      await new Promise((r) => setTimeout(r, 100));
    }
  };

  // Closes the modal via the tour event channel.
  const closeModal = async () => {
    if (!document.querySelector('[data-tour="campaign-basic-details-section"]') &&
        !document.querySelector('[data-tour="campaign-sequence-list-section"]') &&
        !document.querySelector('[data-tour="campaign-modal-right-panel"]')) return;
    window.dispatchEvent(new CustomEvent("__tour:campaignCloseModal"));
    await new Promise((r) => setTimeout(r, 350));
  };

  // Waits for the marketing submenu Campaigns link to be visible in the DOM.
  // Uses the same double-rAF + safety-poll pattern as marketingListTour step 5
  // to bridge the MUI Fade timing gap.
  const waitForCampaignsSubTab = async () => {
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    await new Promise((resolve) => {
      const deadline = Date.now() + 2000;
      const check = () => {
        const el = document.querySelector('[data-tour="campaigns-sub-tab"]');
        if (el) {
          let ok = true;
          for (let p = el; p && p !== document.body; p = p.parentElement) {
            const cs = window.getComputedStyle(p);
            if (cs.display === "none" || cs.visibility === "hidden") {
              ok = false;
              break;
            }
          }
          if (ok) { resolve(); return; }
        }
        if (Date.now() >= deadline) { resolve(); return; }
        setTimeout(check, 50);
      };
      check();
    });
    // MUI Grow entering transition ~225ms — wait 300ms so spotlight shows after it settles.
    await new Promise((r) => setTimeout(r, 300));
  };

  // ── Steps ───────────────────────────────────────────────────────────────────

  return [
    // Step 1: Open Marketing submenu → spotlight Campaigns sub-tab.
    // Escape is already blocked by GuidedTourProvider when a MuiBackdrop is present.
    {
      target: '[data-tour="campaigns-sub-tab"]',
      placement: "right",
      title: "Your Campaigns Overview",
      content:
        "This is where everything connects. Your central hub to create, manage, and monitor all your outreach campaigns.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      before: async () => {
        await closeOpenMenus();
        const marketingNav = document.querySelector('[data-tour="marketing-nav"]');
        if (marketingNav) marketingNav.click();
        await waitForCampaignsSubTab();
      },
    },

    // Step 2: Center tooltip — Campaigns page overview.
    {
      target: "body",
      placement: "center",
      title: "Your Campaigns at a Glance",
      content:
        "View, manage, and get insights from all your campaigns right here. Create new ones too, all in one place.",
      blockTargetInteraction: true,
      before: async () => {
        await closeOpenMenus();
        await ensureOnCampaignsPage();
      },
    },

    // Step 3: Spotlight the Create Campaign button.
    {
      target: '[data-tour="create-campaign-btn"]',
      placement: "bottom",
      title: "Create a Campaign",
      content:
        "Let's walk through the most important things to know as you set up a campaign.",
      blockTargetInteraction: true,
      spotlightPadding: 3,
      spotlightRadius: 6,
      before: async () => {
        await closeModal();
        await ensureOnCampaignsPage();
      },
    },

    // Step 4: Modal Step 1 — spotlight basic-details section, name pre-filled.
    // Tooltip on the left.
    {
      target: '[data-tour="campaign-basic-details-section"]',
      placement: "left",
      title: "Name Your Campaign",
      content:
        "Give your campaign a name, then pick the mailbox(es) you want to send your outreach emails from.",
      blockTargetInteraction: true,
      spotlightPadding: 1,
      spotlightRadius: 6,
      before: async () => {
        await ensureOnCampaignsPage();
        await ensureModalOpen();
        await setModalStep(0);
        // Wait for the ESP list API to resolve so the section has its final
        // height before the spotlight locks on (avoids jumping spotlight).
        await waitForStable('[data-tour="campaign-basic-details-section"]');
      },
    },

    // Step 5: Spotlight the Send Via section in modal Step 1.
    {
      target: '[data-tour="campaign-send-via-section"]',
      placement: "left",
      title: "Choose Your Outreach Mailbox(es)",
      content:
        "Your connected mailbox appears here. Make sure one is connected before launching!",
      blockTargetInteraction: true,
      spotlightPadding: 0,
      spotlightRadius: 6,
      before: async () => {
        await ensureOnCampaignsPage();
        await ensureModalOpen();
        await setModalStep(0);
        // Wait for ESP list to finish loading so the send-via area has its
        // final height — fixes the mispositioned spotlight on resume.
        await waitForStable('[data-tour="campaign-send-via-section"]');
      },
    },

    // Step 6: Advance modal to Step 2 — spotlight full Sequence & List section.
    {
      target: '[data-tour="campaign-sequence-list-section"]',
      placement: "left",
      title: "How Sequences Work",
      content:
        "A Sequence is simply a set of AI-powered steps that your campaign follows automatically for outreach. For example, Step 1 an intro email, Step 2 a follow-up call with an auto-generated script 2 days later, and much more.",
      blockTargetInteraction: true,
      spotlightPadding: 0,
      spotlightRadius: 6,
      before: async () => {
        await ensureOnCampaignsPage();
        await ensureModalOpen();
        await setModalStep(1);
      },
    },

    // Step 7: Spotlight Contact List dropdown within Step 2.
    {
      target: '[data-tour="campaign-contact-list"]',
      placement: "left",
      title: "Attach Your Audience",
      content:
        "This is where you plug in your Marketing List, the exact audience you want to reach out to in this campaign.",
      blockTargetInteraction: true,
      spotlightPadding: 6,
      spotlightRadius: 6,
      before: async () => {
        await ensureOnCampaignsPage();
        await ensureModalOpen();
        await setModalStep(1);
      },
    },

    // Step 8: Spotlight ICP Persona section within Step 2.
    {
      target: '[data-tour="campaign-icp-section"]',
      placement: "left",
      title: "Laser-Focus with ICP Matching",
      content:
        "Enable this and your campaign reaches only the people who truly fit your Ideal Customer Profile.",
      blockTargetInteraction: true,
      spotlightPadding: 2,
      spotlightRadius: 6,
      before: async () => {
        await ensureOnCampaignsPage();
        await ensureModalOpen();
        await setModalStep(1);
      },
    },

    // Step 9: Close modal → center tooltip wrapping up.
    {
      target: "body",
      placement: "center",
      title: "You're Ready to Run Campaigns",
      content:
        "That covers the key things to know! Once created, open your campaign to schedule it and track email delivery, open rates, replies, and much more.",
      blockTargetInteraction: true,
      before: async () => {
        await closeModal();
        await ensureOnCampaignsPage();
      },
    },
  ];
}
